import { useInfiniteQuery } from '@tanstack/vue-query';
import { useScroll } from '@vueuse/core';
import type { Ref, ComputedRef, MaybeRefOrGetter } from 'vue';

type PaginationType = 'cursor' | 'page';
type Scope = 'tenant' | 'central';

interface UseInfinitePaginationOptions {
    url: MaybeRefOrGetter<string>;
    queryKey: ComputedRef<any[]> | Ref<any[]>;
    filters: ComputedRef<Record<string, any>> | Ref<Record<string, any>> | Record<string, any>;
    scope?: Scope;
    enabled?: ComputedRef<boolean> | Ref<boolean> | boolean;
    paginationType?: PaginationType;
    transformParams?: (params: Record<string, any>) => Record<string, any>;
    transformResponse?: (response: any) => any;
    scrollContainer?: Ref<HTMLElement | null>;
}

export function useInfinitePagination(options: UseInfinitePaginationOptions) {
    const {
        url,
        queryKey,
        filters,
        scope = 'tenant',
        enabled = true,
        paginationType = 'cursor',
        transformParams,
        transformResponse,
        scrollContainer,
    } = options;

    const axios = computed(() => (scope === 'tenant' ? window.tenantAxios : window.centralAxios));

    const resolvedFilters = computed(() => unref(filters));
    const resolvedEnabled = computed(() => unref(enabled));

    const queryFn = async ({ pageParam }: { pageParam: any }) => {
        const filtersValue = resolvedFilters.value;
        const params: Record<string, any> = {
            ...filtersValue,
        };

        if (paginationType === 'cursor') {
            if (pageParam) {
                params.cursor = pageParam;
            }
        } else {
            params.page = pageParam;
        }

        const processedParams = Object.fromEntries(
            Object.entries(params).map(([key, val]) => [key, Array.isArray(val) ? val.join(',') : val]),
        );

        const finalParams = transformParams ? transformParams(processedParams) : processedParams;

        const { data: response } = await axios.value.get(toValue(url), {
            params: finalParams,
        });

        if (transformResponse) {
            return transformResponse(response);
        }

        const data =
            Array.isArray(response?.data) ? response.data
            : Array.isArray(response) ? response
            : [];
        const meta = response?.meta || {};

        return {
            data,
            meta,
        };
    };

    const getNextPageParam = (lastPage: any) => {
        if (paginationType === 'cursor') {
            return lastPage?.meta?.next_cursor;
        }
        const { current_page, last_page } = lastPage?.meta || {};
        return current_page < last_page ? current_page + 1 : undefined;
    };

    const infiniteQuery = useInfiniteQuery({
        queryKey: computed(() => [...unref(queryKey)]),
        queryFn,
        getNextPageParam,
        initialPageParam: paginationType === 'cursor' ? null : 1,
        enabled: resolvedEnabled,
        retry: false,
        refetchOnWindowFocus: false,
    });

    const accumulatedData = computed(
        () => infiniteQuery.data.value?.pages?.flatMap((page: any) => page.data || []) ?? [],
    );

    const scrollState = scrollContainer ? useScroll(scrollContainer, { behavior: 'smooth' }) : null;

    if (scrollState) {
        watch(
            () => scrollState.arrivedState.bottom,
            (isBottom) => {
                if (
                    isBottom &&
                    infiniteQuery.hasNextPage.value &&
                    !infiniteQuery.isFetchingNextPage.value &&
                    !infiniteQuery.isLoading.value
                ) {
                    infiniteQuery.fetchNextPage();
                }
            },
        );
    }

    return {
        ...infiniteQuery,
        accumulatedData,
        arrivedState: scrollState?.arrivedState,
    };
}
